import { Collapse, Grid } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCitiesAndZipsByStates } from 'app/services/SharedServices/SharedServices';
import { getSortedData, getUnique } from 'app/helpers/arrayHelpers';
import {
  selectFilters,
  selectGridFiltersExpanded,
  selectGridState,
} from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { FilterType } from 'app/enums/Common';
import { IStateCityZip } from 'app/models/Candidate/IStateCityZip';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lookupOptionSelector } from 'store/redux-store/lookup/lookup.selectors';
import { ILookupOptions } from 'store/redux-store/lookup/initialState';
import { getAllCitiesAndZips } from '@AMIEWEB/GlobalSearch/Candidate/FilterUtils';
import { CustomFilter } from 'app/ComponentLibrary/Filter/CustomFilter';
import { trackPromise } from 'react-promise-tracker';
import { makeStyles } from 'tss-react/mui';
import { initialFilterOrderChips, positionOpts, SearchOrderFilterSpecifications } from './OrdersSearchFilterUtils';
import { selectSkillSetTree } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.selector';

export const useStyles = makeStyles()(theme => ({
  filterCollapse: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
}));

export interface IOptions {
  id: number;
  name: string;
  value: string;
}

export const OrdersSearchFilter = props => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset } = useFormContext();
  const [allSpecialties, setAllSpecialties] = useState<any[]>([]);
  const [allSubSpecialties, setAllSubSpecialties] = useState<any[]>([]);
  const gridStateFilters = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const placementDetails = useSelector(selectPlacementDetails);
  const loadedData = useSelector(lookupOptionSelector) as ILookupOptions;
  const allCitiesAndZipsData = useRef<IStateCityZip[]>();
  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const candidateSkillSets = useSelector(selectSkillSetTree)?.candidateSkillSets;

  const travelerSkillSets = useMemo(() => {
    const disc: IOptions[] = [];
    const spec: IOptions[] = [];
    const subSpec: IOptions[] = [];
    candidateSkillSets?.forEach(discipline => {
      if (discipline.verified) {
        if (discipline.specialties?.filter(item => item.verified).length > 0) {
          discipline.specialties?.forEach(specialty => {
            if (specialty.verified) {
              if (specialty.subSpecilities?.filter(item => item.verified).length > 0) {
                specialty.subSpecilities?.forEach(subspecialty => {
                  if (subspecialty.verified) {
                    disc.push({
                      id: discipline.disciplineId,
                      name: discipline.abbreviation,
                      value: discipline.disciplineId.toString(),
                    });
                    spec.push({
                      id: specialty.specilityId,
                      name: specialty.description,
                      value: specialty.specilityId.toString(),
                    });
                    subSpec.push({
                      id: subspecialty.subSpecialtyID,
                      name: subspecialty.description,
                      value: subspecialty.subSpecialtyID.toString(),
                    });
                  }
                });
              } else {
                disc.push({
                  id: discipline.disciplineId,
                  name: discipline.abbreviation,
                  value: discipline.disciplineId.toString(),
                });
                spec.push({
                  id: specialty.specilityId,
                  name: specialty.description,
                  value: specialty.specilityId.toString(),
                });
              }
            }
          });
        } else {
          disc.push({
            id: discipline.disciplineId,
            name: discipline.abbreviation,
            value: discipline.disciplineId.toString(),
          });
        }
      }
      if (discipline.unverified) {
        if (discipline.specialties?.filter(item => item.unverified).length > 0) {
          discipline.specialties?.forEach(specialty => {
            if (specialty.unverified) {
              if (specialty.subSpecilities?.filter(item => item.unverified).length > 0) {
                specialty.subSpecilities?.forEach(subspecialty => {
                  if (subspecialty.unverified) {
                    disc.push({
                      id: discipline.disciplineId,
                      name: discipline.abbreviation,
                      value: discipline.disciplineId.toString(),
                    });
                    spec.push({
                      id: specialty.specilityId,
                      name: specialty.description,
                      value: specialty.specilityId.toString(),
                    });
                    subSpec.push({
                      id: subspecialty.subSpecialtyID,
                      name: subspecialty.description,
                      value: subspecialty.subSpecialtyID.toString(),
                    });
                  }
                });
              } else {
                disc.push({
                  id: discipline.disciplineId,
                  name: discipline.abbreviation,
                  value: discipline.disciplineId.toString(),
                });
                spec.push({
                  id: specialty.specilityId,
                  name: specialty.description,
                  value: specialty.specilityId.toString(),
                });
              }
            }
          });
        } else {
          disc.push({
            id: discipline.disciplineId,
            name: discipline.abbreviation,
            value: discipline.disciplineId.toString(),
          });
        }
      }
    });
    return {
      disciplines: disc
        ? _.uniqBy(disc, function (e) {
            return e.id;
          })
        : [],
      specialties: spec
        ? _.uniqBy(spec, function (e) {
            return e.id;
          })
        : [],
      subSpecialties: subSpec
        ? _.uniqBy(subSpec, function (e) {
            return e.id;
          })
        : [],
    };
  }, [candidateSkillSets]);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  const gridSpecs = useRef(
    new SearchOrderFilterSpecifications(
      {
        divisionOpts: [],
        disciplineOpts: [],
        specialtiesOpts: [],
        subSpecialtiesOpts: [],
        facilityStatusesOpts: [],
        orderTypesOpts: [],
        orderPrioritiesOpts: [],
        statesOpts: [],
        compactStatesOpts: [],
        shiftOpts: [],
        accountManagerOpts: [],
        cityOptions: [],
      },
      gridStateFilters,
    ),
  );
  const [fields, setFields] = React.useState(gridSpecs.current.getSearchOrderFilterSpecifications());

  const getMultiSelectOptions = (options, labelPrefix?, labelPostfix?, withoutAllOption?: boolean) => {
    const opts = (options || []).map((opt, index) => ({
      id: index + 1,
      labelPrefix,
      labelPostfix,
      ...opt,
    }));
    if (!withoutAllOption) opts.unshift({ id: 0, name: 'All', value: 'All', labelPrefix });
    return opts;
  };
  const getDisciplinesMultiSelectOptions = options => {
    const array = options?.reduce(
      (resultArray, item) => [
        ...resultArray,
        {
          value: item?.value?.slice(item?.value?.indexOf('_') + 1),
          name: item?.name,
          corelation: item?.value,
        },
      ],
      [],
    );
    const sortedData = getSortedData(array || [], FilterType.string);
    return sortedData;
  };

  const findSpecialties = disciplines => {
    const specialtiesArray: any[] = [];
    if (disciplines?.length > 0) {
      disciplines?.forEach(item => {
        const specialties = allSpecialties.filter(
          curr => curr?.corelation?.slice(0, curr?.corelation?.indexOf('_')) === item?.value,
        );
        if (specialties) {
          specialtiesArray.push(...specialties);
        }
      });
      const sortedData = getSortedData(specialtiesArray, FilterType.string);
      const uniqueSpecialties = getUnique(
        sortedData?.filter(x => x.name),
        'name',
      );
      if (uniqueSpecialties?.length >= 1) {
        gridSpecs.current.filterSpecs.specialtiesOpts = getMultiSelectOptions(uniqueSpecialties);
        const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
        setFields(fieldsSchema);
        findSubSpecialties(specialtiesArray);
      } else {
        gridSpecs.current.filterSpecs.subSpecialtiesOpts = [];
        gridSpecs.current.filterSpecs.specialtiesOpts = getMultiSelectOptions(uniqueSpecialties);
        const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
        setFields(fieldsSchema);
      }
    } else {
      gridSpecs.current.filterSpecs.specialtiesOpts = getUnique(
        allSpecialties?.filter(x => x.name),
        'name',
      );
      const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
      setFields(fieldsSchema);
      findSubSpecialties([]);
    }
  };

  const findSubSpecialties = specialties => {
    const subSpecialtiesArray: any[] = [];
    if (specialties?.length > 0) {
      specialties?.forEach(item => {
        allSubSpecialties?.forEach(curr => {
          if (
            curr?.corelation?.substr(0, curr?.corelation?.lastIndexOf('_')) ===
            item?.corelation?.slice(item?.corelation?.indexOf('_') + 1)
          ) {
            if (curr?.name !== 'All') {
              subSpecialtiesArray.push(curr);
            }
          }
        });
      });
      const sortedData = getSortedData(subSpecialtiesArray, FilterType.string);
      const uniqueSubSpecialties = getUnique(
        sortedData?.filter(x => x.name),
        'name',
      );
      gridSpecs.current.filterSpecs.subSpecialtiesOpts = getMultiSelectOptions(uniqueSubSpecialties);
      const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
      setFields(fieldsSchema);
    } else {
      gridSpecs.current.filterSpecs.subSpecialtiesOpts = getUnique(
        allSubSpecialties?.filter(x => x.name),
        'name',
      );
    }
  };

  const fetchOrderFilterSpecs = async () => {
    const divisionOpts = getMultiSelectOptions(loadedData?.divisionOptions || []);
    const disciplineOpts = getMultiSelectOptions(loadedData?.disciplineOptions || []);
    const allSpecialtiesOpts = getMultiSelectOptions(
      getDisciplinesMultiSelectOptions(loadedData?.specialtiesOptions || []),
    );
    const specialtiesOpts = getUnique(
      allSpecialtiesOpts?.filter(x => x.name),
      'name',
    );
    setAllSpecialties(allSpecialtiesOpts);

    const allSubSpecialtiesOpts = getMultiSelectOptions(
      getDisciplinesMultiSelectOptions(loadedData?.subSpecialtiesOptions) || [],
    );
    const subSpecialtiesOpts = getUnique(
      allSubSpecialtiesOpts?.filter(x => x.name),
      'name',
    );
    setAllSubSpecialties(allSubSpecialtiesOpts);

    const facilityStatusesOpts = getMultiSelectOptions(loadedData?.facilityStatusOptions || []);
    const orderTypesOpts = getMultiSelectOptions(loadedData?.orderTypeOptions || []);
    const orderPrioritiesOpts = getMultiSelectOptions(loadedData?.orderPriorityOptions || []);
    const statesLookups = loadedData?.stateOptions;
    const states = getMultiSelectOptions(statesLookups || []);
    const compactStatesOpts = loadedData?.compactStateOptions;
    const compactStates = compactStatesOpts?.map(item => {
      const stateAbbreviations = item.name?.split(',');
      return {
        name: item?.value?.replace('?', "'")?.replace('Only ', '')?.replace('LIC', 'Licence'),
        value: states.filter(s => stateAbbreviations.includes(s.value)),
        isCompactOpt: true,
      };
    });
    const statesOpts = getMultiSelectOptions([...compactStates, ...statesLookups] || []);
    const shiftOpts = getMultiSelectOptions(loadedData?.shiftListOptions || []);
    const accountManagerOpts = getMultiSelectOptions(loadedData?.accountManagerOptions || []);

    allCitiesAndZipsData.current = (await getCitiesAndZipsByStates({
      states: [statesLookups?.[0]?.name],
      cities: undefined,
    })) as IStateCityZip[];
    const AllCitiesAndZips = getAllCitiesAndZips(allCitiesAndZipsData.current);
    const cityOpts = AllCitiesAndZips?.cities;

    gridSpecs.current.filterSpecs = {
      divisionOpts,
      disciplineOpts,
      specialtiesOpts,
      subSpecialtiesOpts,
      facilityStatusesOpts,
      orderTypesOpts,
      orderPrioritiesOpts,
      statesOpts,
      compactStatesOpts,
      shiftOpts,
      accountManagerOpts,
      cityOptions: cityOpts,
    };
    const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
    setFields(fieldsSchema);
  };

  const updateGridState = useCallback(
    async (selectedStates: string[], selectedCities: string[], gridStateFilter: any) => {
      if (!gridStateFilter || !allCitiesAndZipsData.current) return;
      let filteredCities: IStateCityZip[];
      if (!selectedStates.length) filteredCities = allCitiesAndZipsData.current;
      else {
        filteredCities = (await getCitiesAndZipsByStates({
          states: selectedStates,
          cities: [],
        })) as IStateCityZip[];
      }
      gridSpecs.current.filterSpecs.cityOptions = getAllCitiesAndZips(filteredCities).cities;
      gridSpecs.current.gridStateFilters = gridStateFilter;
      const fieldsSchema = gridSpecs.current.getSearchOrderFilterSpecifications();
      setFields(fieldsSchema);
    },
    [],
  );

  const defaultAppliedFilters = {
    ...initialFilterOrderChips,
    facilityIds: [
      {
        id: placementDetails?.header?.facility?.id,
        value: placementDetails?.header?.facility?.id?.toString(),
        name:
          placementDetails?.header?.facility?.name?.toString() +
          ', ' +
          placementDetails?.header?.facility?.id?.toString(),
      },
    ],
    divisionIds: [
      {
        id: placementDetails?.header?.facility?.amnDivisionTypeId,
        name: placementDetails?.header?.facility?.amnDivisionType,
        value: placementDetails?.header?.facility?.amnDivisionTypeId.toString(),
      },
    ],
    disciplineIds: travelerSkillSets?.disciplines,
    specialtyIds: travelerSkillSets?.specialties,
    subSpecialtyIds: travelerSkillSets?.subSpecialties,
    amUserIds: placementDetails?.header?.accountManagerId
      ? getMultiSelectOptions(loadedData?.accountManagerOptions).filter(
          x => x.value === placementDetails?.header?.accountManagerId.toString(),
        )
      : null,
    positions: positionOpts[0],
  };

  const resetForm = () => {
    reset({ ...defaultAppliedFilters });
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(defaultAppliedFilters),
        selectedChipFilters: {},
      }),
    );
    props.onApplyFilter(defaultAppliedFilters);
  };

  useEffect(() => {
    findSpecialties(gridStateFilters?.filters?.disciplineIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridStateFilters?.filters?.disciplineIds]);

  useEffect(() => {
    if (gridStateFilters?.filters?.specialtyIds) {
      findSubSpecialties(gridStateFilters?.filters?.specialtyIds);
    } else {
      findSpecialties(gridStateFilters?.filters?.disciplineIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridStateFilters?.filters?.specialtyIds]);

  useEffect(() => {
    if (!gridStateFilters || !allCitiesAndZipsData.current) return;
    const selectedStates = !(gridStateFilters?.filters?.states as any[])?.length
      ? []
      : (gridStateFilters?.filters?.states as any[]).reduce(
          (result, item) => (!item?.isCompactOpt ? [...result, item] : result),
          [],
        );

    const selectedCities = (
      !(gridStateFilters?.filters?.city as any[])?.length ? [] : gridStateFilters?.filters?.city
    ) as any[];
    updateGridState(
      selectedStates.map(x => x.name),
      selectedCities.map(x => x.name),
      gridStateFilters,
    );
  }, [gridStateFilters, updateGridState]);

  useEffect(() => {
    trackPromise(fetchOrderFilterSpecs(), 'gloabl-search-call');
    resetForm();
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={10.8}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields}
            justifyContent={'flex-start'}
            version2
            resetBtn
            analytics={{
              caller: t('Change order Id Search'),
              onApply: true,
              properties: {
                category: SearchType.order,
              },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item xs={1.2} className={classes.filterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          checked={filtersExpanded}
          onResetForm={() => resetForm()}
          onAdvancedFilterClick={() => handleChange()}
        />
      </Grid>
    </Grid>
  );
};
